import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { SectionCard } from '../../shared/Panel';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_ORDER_CHANGE_REGISTER,
  ORDER_CHANGE_FIELDS,
  ORDER_CHANGE_ORIGINS,
  fetchOrderChanges,
  notifyOrderChangesChanged,
  reportOrderChange,
  toErrorMessage,
  type OrderChangesForOrder,
  type ReportInput,
} from './orderChangesApi';
import { MIN_REASON, OrderChangeItem } from './OrderChangeItem';

/**
 * „Zmiany zlecenia” na karcie zlecenia (makieta back office v2, fala B, B9;
 * przełącznik `orderChangeRegister`, S-21.10-09; ścieżki MIX-02, ADM-04).
 * Zgłoszenie „było → ma być” (CS albo osoba realizująca), decyzja stron
 * z powodem, „Wprowadzone w TMS” i zamknięcie importem — historia przy
 * zleceniu. Do TMS nic nie wychodzi z aplikacji. Wyłączony przełącznik —
 * sekcji nie ma.
 */

type Draft = Omit<ReportInput, 'userOfferId'>;

const EMPTY: Draft = { field: '', origin: 'client', reason: '' };

/** Pola „ma być” zależne od rodzaju zmiany. */
const RequestedFields = ({
  draft,
  set,
  busy,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  busy: boolean;
}) => {
  const input = (key: keyof Draft, label: string, type = 'text', testId?: string) => (
    <TextField
      size="small"
      label={label}
      type={type}
      value={(draft[key] as string | undefined) ?? ''}
      disabled={busy}
      onChange={(e) => set({ [key]: e.target.value })}
      slotProps={{
        inputLabel: type === 'datetime-local' ? { shrink: true } : undefined,
        htmlInput: { 'data-testid': testId ?? `order-change-${key}` },
      }}
    />
  );
  switch (draft.field) {
    case 'loadingWindow':
    case 'unloadingWindow':
      return (
        <Stack direction="row" spacing={1}>
          {input('from', 'Ma być: od (czas polski)', 'datetime-local')}
          {input('to', 'Ma być: do (czas polski)', 'datetime-local')}
        </Stack>
      );
    case 'loadingPlace':
    case 'unloadingPlace':
      return (
        <Stack direction="row" spacing={1}>
          {input('country', 'Kraj (2 litery)')}
          {input('postalCode', 'Kod pocztowy')}
          {input('city', 'Miejscowość')}
        </Stack>
      );
    case 'weight':
      return input('value', 'Ma być: waga (kg)', 'number');
    case 'ldm':
      return input('value', 'Ma być: LDM', 'number');
    case 'price':
      return (
        <Stack direction="row" spacing={1}>
          {input('amount', 'Ma być: stawka', 'number')}
          {input('currency', 'Waluta (np. EUR)')}
        </Stack>
      );
    case 'notes':
      return input('text', 'Ma być: opis zmiany');
    default:
      return null;
  }
};

const ReportDialog = ({
  open,
  userOfferId,
  onClose,
  onSaved,
}: {
  open: boolean;
  userOfferId: number;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const notify = useNotify();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const save = () =>
    void (async () => {
      setBusy(true);
      try {
        await reportOrderChange({ ...draft, userOfferId });
        notify('Zmiana zgłoszona — czeka na decyzję', { type: 'success' });
        setDraft(EMPTY);
        onSaved();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Zgłoś zmianę zlecenia</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          „Było” weźmiemy z danych zlecenia. Po decyzji zmianę wprowadza w TMS przyjęcie zleceń — aplikacja do TMS
          nic nie zapisuje, a zmianę zamknie import, gdy TMS pokaże nową wartość.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            label="Co się zmienia"
            value={draft.field}
            disabled={busy}
            onChange={(e) => setDraft({ ...EMPTY, origin: draft.origin, reason: draft.reason, field: e.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'order-change-field' } }}
          >
            {ORDER_CHANGE_FIELDS.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
          <RequestedFields draft={draft} set={set} busy={busy} />
          <TextField
            select
            size="small"
            label="Skąd zmiana"
            value={draft.origin}
            disabled={busy}
            onChange={(e) => set({ origin: e.target.value })}
          >
            {ORDER_CHANGE_ORIGINS.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Powód i źródło (np. mail klienta)"
            multiline
            minRows={2}
            value={draft.reason}
            disabled={busy}
            onChange={(e) => set({ reason: e.target.value })}
            helperText={`Co najmniej ${MIN_REASON} znaków`}
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'order-change-reason' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          data-testid="order-change-report-save"
          disabled={busy || draft.field === '' || draft.reason.trim().length < MIN_REASON}
          onClick={save}
        >
          Zgłoś zmianę
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionBody = ({ record }: { record: RaRecord }) => {
  const refresh = useRefresh();
  const id = Number(record.id);
  const [state, setState] = useState<OrderChangesForOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await fetchOrderChanges(id));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const changed = () => {
    void load();
    // Wpis w historii zmian zlecenia — karta pokazuje go po odświeżeniu.
    refresh();
  };

  return (
    <SectionCard title="Zmiany zlecenia (było → ma być)">
      <Stack spacing={1} data-testid="order-changes-section">
        {state?.canReport ? (
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditNoteIcon />}
              data-testid="order-change-report"
              onClick={() => setOpen(true)}
            >
              Zgłoś zmianę
            </Button>
          </Box>
        ) : null}
        {state && !state.canReport && state.reportRefusal ? (
          <Typography variant="caption" color="text.secondary">
            {state.reportRefusal}
          </Typography>
        ) : null}
        {error ? <Alert severity="warning">{error}</Alert> : null}
        {state === null && !error ? <CircularProgress size={18} /> : null}
        {state && state.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak zgłoszonych zmian.
          </Typography>
        ) : null}
        {state?.items.map((c) => <OrderChangeItem key={c.id} change={c} onChanged={changed} />)}
      </Stack>
      <ReportDialog
        open={open}
        userOfferId={id}
        onClose={() => setOpen(false)}
        onSaved={() => {
          setOpen(false);
          notifyOrderChangesChanged();
          changed();
        }}
      />
    </SectionCard>
  );
};

/** Sekcja na karcie zlecenia — za przełącznikiem. */
export const OrderChangesSection = () => {
  const record = useRecordContext<RaRecord>();
  const on = useFeature(FEATURE_ORDER_CHANGE_REGISTER);
  if (!on || !record) return null;
  return <SectionBody record={record} />;
};
